import { queryOptions } from "@tanstack/react-query";
import { notFound } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { getApiClient, getSessionCookieHeader } from "~/utils/api-client";

export const CONTRACTS_KEYS = {
	all: ["contracts"] as const,
	lists: () => [...CONTRACTS_KEYS.all, "list"] as const,
	pending: () => [...CONTRACTS_KEYS.lists(), "pending"] as const,
	signed: () => [...CONTRACTS_KEYS.lists(), "signed"] as const,
	signingView: (envelopeId: string) =>
		[...CONTRACTS_KEYS.all, "signing-view", envelopeId] as const,
};

const fetchPendingContracts = createServerFn({
	method: "GET",
	// Hono types unknown JSON fields broadly; the API response is already JSON.
	strict: { output: false },
}).handler(async () => {
	const api_client = getApiClient(getSessionCookieHeader());
	const result = await api_client.self.api.contracts.$get();

	if (!result.ok) {
		throw new Error("Failed to fetch pending contracts");
	}

	return await result.json();
});

export type PendingContract = Awaited<
	ReturnType<typeof fetchPendingContracts>
>[number];

export const pendingContractsQueryOptions = queryOptions({
	queryKey: CONTRACTS_KEYS.pending(),
	queryFn: () => fetchPendingContracts(),
	// Don't refetch on every tab-switch/focus — the contracts list rarely
	// changes faster than a minute, and the signing-view query still
	// refetches on focus to catch state changes on an active envelope.
	staleTime: 60_000,
});

const fetchSignedContracts = createServerFn({
	method: "GET",
	strict: { output: false },
}).handler(async () => {
	const api_client = getApiClient(getSessionCookieHeader());
	const result = await api_client.self.api.contracts.signed.$get();

	if (!result.ok) {
		throw new Error("Failed to fetch signed contracts");
	}

	return await result.json();
});

export const signedContractsQueryOptions = queryOptions({
	queryKey: CONTRACTS_KEYS.signed(),
	queryFn: () => fetchSignedContracts(),
	staleTime: 60_000,
});

export const fetchContractDownloadUrl = createServerFn({ method: "GET" })
	.validator(z.string().min(1))
	.handler(async ({ data: envelope_id }) => {
		const api_client = getApiClient(getSessionCookieHeader());
		const result = await api_client.self.api.contracts[
			":envelope_id"
		].download.$get({
			param: { envelope_id },
		});

		if (!result.ok) {
			throw new Error("Failed to fetch download URL");
		}

		return await result.json();
	});

const fetchSigningView = createServerFn({
	method: "GET",
	strict: { output: false },
})
	.validator(z.string().min(1))
	.handler(async ({ data: envelope_id }) => {
		const api_client = getApiClient(getSessionCookieHeader());
		const result = await api_client.self.api.contracts[
			":envelope_id"
		].sign.$get({
			param: { envelope_id },
		});

		if (!result.ok) {
			if (result.status === 404) {
				throw notFound();
			}
			throw new Error("Failed to fetch signing view");
		}

		return await result.json();
	});

export type SigningView = Awaited<ReturnType<typeof fetchSigningView>>;

export const signingViewQueryOptions = (envelopeId: string) =>
	queryOptions({
		queryKey: CONTRACTS_KEYS.signingView(envelopeId),
		queryFn: () => fetchSigningView({ data: envelopeId }),
		enabled: !!envelopeId,
		// Re-fetch when the user returns to the tab — the envelope may have
		// been completed, declined, or voided by another signer while they
		// were away, and the handler's state guards need fresh data to
		// render the correct "already done" screen instead of a stale
		// signing UI that would 409 on submit.
		refetchOnWindowFocus: true,
	});
